import { DEFAULT_TEAM_ICON_COLOR, getTeamIcon } from '@/utils/teamIcons';
import { cn } from '@startupquickstart/frontend';
import { ImageIcon, TrendingDown, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';

interface ActivityData {
  id: string;
  type: string;
  message: string;
  description?: string;
  teamId: string | null;
  teamName: string | null;
  teamIconName?: string;
  teamIconColor?: string;
  challengeId?: string | null;
  challengeName?: string | null;
  points?: number | null;
  photoUrl?: string | null;
  reason?: string;
  createdAt: string;
}

interface DisplayActivityCardProps {
  activity: ActivityData;
  isNew?: boolean;
  pointsLabel: string;
}

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  return `${Math.floor(diffInHours / 24)}d ago`;
}

/**
 * A single completion / adjustment card on the display activity feed —
 * a deck-language surface card themed by the `--dsp-*` variables.
 */
export function DisplayActivityCard({ activity, isNew, pointsLabel }: DisplayActivityCardProps) {
  const TeamIcon = useMemo(() => getTeamIcon(activity.teamIconName), [activity.teamIconName]);
  const relativeTime = useMemo(() => getRelativeTime(activity.createdAt), [activity.createdAt]);

  if (activity.type !== 'challenge_completed' && activity.type !== 'points_adjusted') {
    return null;
  }

  const isPointsAdjustment = activity.type === 'points_adjusted';
  const isPositiveAdjustment = (activity.points ?? 0) >= 0;

  return (
    <div
      className={cn('rounded-2xl border p-4 transition-all duration-300', isNew && 'animate-pulse')}
      style={{
        backgroundColor: 'var(--dsp-surface)',
        borderColor: 'var(--dsp-line)',
        boxShadow: isNew ? '0 0 0 2px var(--dsp-accent)' : undefined,
      }}
    >
      <div className="flex gap-4">
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: activity.teamIconColor || DEFAULT_TEAM_ICON_COLOR }}
        >
          <TeamIcon className="h-5 w-5 text-white" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate font-semibold">{activity.teamName || 'Unknown Team'}</span>
            <span
              className="flex-shrink-0 font-mono text-[0.625rem] uppercase tracking-[0.15em]"
              style={{ color: 'var(--dsp-soft)' }}
            >
              {relativeTime}
            </span>
          </div>

          {isPointsAdjustment ? (
            <p className="mt-0.5 text-sm" style={{ color: 'var(--dsp-soft)' }}>
              Points adjusted: {activity.reason || activity.description || 'Manual adjustment'}
            </p>
          ) : (
            <p className="mt-0.5 truncate text-sm" style={{ color: 'var(--dsp-soft)' }}>
              Completed{' '}
              <span className="text-base font-semibold" style={{ color: 'var(--dsp-text)' }}>
                {activity.challengeName || 'a challenge'}
              </span>
            </p>
          )}

          {activity.points != null && (isPointsAdjustment || activity.points > 0) && (
            <div className="mt-2 inline-flex items-baseline gap-1.5">
              {isPointsAdjustment &&
                (isPositiveAdjustment ? (
                  <TrendingUp className="h-3.5 w-3.5 self-center" style={{ color: 'var(--dsp-accent-text)' }} />
                ) : (
                  <TrendingDown className="h-3.5 w-3.5 self-center text-red-400" />
                ))}
              <span
                className="text-lg font-semibold tabular-nums"
                style={{
                  color:
                    isPointsAdjustment && !isPositiveAdjustment
                      ? '#f87171'
                      : 'var(--dsp-accent-text)',
                }}
              >
                {isPositiveAdjustment ? '+' : ''}
                {activity.points.toLocaleString()}
              </span>
              <span
                className="font-mono text-[0.625rem] uppercase tracking-[0.15em]"
                style={{ color: 'var(--dsp-soft)' }}
              >
                {pointsLabel}
              </span>
            </div>
          )}
        </div>

        {activity.photoUrl && (
          <div className="flex-shrink-0">
            <div
              className="relative h-16 w-16 overflow-hidden rounded-xl border"
              style={{ borderColor: 'var(--dsp-line)' }}
            >
              <img
                src={activity.photoUrl}
                alt="Submission"
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity hover:opacity-100">
                <ImageIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
